import { SlackNotificationType } from "@/lib/shared/enums";

/**
 * Hard cap on each Slack webhook POST. Slack notifications are best-effort, and this runs inside a
 * deferred `after()` callback — without a timeout a stalled webhook would pin the serverless instance
 * open (and the fallback would double the exposure with a second unbounded fetch).
 */
const WEBHOOK_TIMEOUT_MS = 5000;

/** A single Slack Block Kit block (header, section, divider, context, …). Loosely typed by design. */
export type SlackBlock = Record<string, unknown>;

/**
 * Posts a rich Slack message (top-level `text` + Block Kit `blocks`) to a concrete webhook URL,
 * falling back to a plain-text post on failure. Shared by both webhook-resolution strategies below.
 *
 * @param webhookUrl - The destination incoming webhook URL.
 * @param text - Top-level message text and the notification/fallback preview.
 * @param blocks - The Block Kit blocks to render as the message body.
 * @param logLabel - Human-readable destination for log messages (e.g. a `SlackNotificationType` or a channel name).
 */
async function postRichSlackMessage(
  webhookUrl: string,
  text: string,
  blocks: SlackBlock[],
  logLabel: string
): Promise<void> {
  const payload = { text, blocks };

  try {
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(WEBHOOK_TIMEOUT_MS),
    });
    if (!response.ok) {
      throw new Error(`Slack webhook returned ${response.status}: ${await response.text()}`);
    }
    console.log(`Rich Slack notification sent to ${logLabel}.`);
  } catch (error) {
    console.error(`Failed to send rich Slack notification to ${logLabel}; falling back to plain text.`, error);
    await sendPlainTextFallback(webhookUrl, text);
  }
}

/**
 * Sends a rich Slack message — top-level `text` plus a top-level Block Kit `blocks` layout — to the
 * `SLACK_WEBHOOK_URL_<type>` webhook. Posting `blocks` at the top level (rather than nested in an
 * `attachments[]` entry) renders the message as a first-class message instead of an indented
 * secondary attachment, at the cost of the attachment-only colored sidebar. Best-effort and NEVER
 * throws — safe to call from a deferred (`after`) context: a missing webhook URL or a failed POST
 * falls back to a plain-text message, and even that fallback is swallowed.
 *
 * @param type - Which `SLACK_WEBHOOK_URL_<type>` channel to post to.
 * @param text - Top-level message text (put `@mentions` here — they only ping from the top level) and
 *   the notification/fallback preview.
 * @param blocks - The Block Kit blocks to render as the message body.
 * @returns Resolves once the send (or its fallback) has been attempted; never rejects.
 */
export async function sendRichSlackMessageServerOnly(
  type: SlackNotificationType,
  text: string,
  blocks: SlackBlock[]
): Promise<void> {
  const webhookUrl = process.env[`SLACK_WEBHOOK_URL_${type}`];
  if (!webhookUrl) {
    console.error(`SLACK_WEBHOOK_URL_${type} is not defined; falling back to plain text.`);
    await sendPlainTextFallback(webhookUrl, text);
    return;
  }

  await postRichSlackMessage(webhookUrl, text, blocks, `${type} channel`);
}

/**
 * Sends a rich Slack message to a webhook URL resolved by the caller (e.g. from a
 * database-configured notification channel) rather than an env-var-backed
 * `SlackNotificationType`. Same best-effort/never-throws contract as
 * {@link sendRichSlackMessageServerOnly}.
 *
 * @param webhookUrl - The destination incoming webhook URL.
 * @param text - Top-level message text and the notification/fallback preview.
 * @param blocks - The Block Kit blocks to render as the message body.
 * @returns Resolves once the send (or its fallback) has been attempted; never rejects.
 */
export async function sendRichSlackMessageToWebhook(
  webhookUrl: string,
  text: string,
  blocks: SlackBlock[]
): Promise<void> {
  await postRichSlackMessage(webhookUrl, text, blocks, "configured webhook");
}

/**
 * Plain-text fallback for the rich sender: posts `{ text }` to the same webhook. Swallows its own
 * errors (including a missing webhook URL) so a deferred caller can never throw.
 *
 * @param webhookUrl - The resolved webhook URL, or undefined when it isn't configured.
 * @param text - The plain-text message to post.
 * @returns Resolves once the attempt completes; never rejects.
 */
async function sendPlainTextFallback(webhookUrl: string | undefined, text: string): Promise<void> {
  if (!webhookUrl) return;
  try {
    await fetch(webhookUrl, {
      method: "POST",
      body: JSON.stringify({ text }),
      signal: AbortSignal.timeout(WEBHOOK_TIMEOUT_MS),
    });
  } catch (error) {
    console.error("Plain-text Slack fallback also failed.", error);
  }
}
